import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Sparkle, FileArrowDown, CircleNotch, UploadSimple, ChatCircleDots, Phone,
  EnvelopeSimple, Headset, FileAudio, ArrowClockwise, X, TrendUp, TrendDown,
  ArrowRight, Lightbulb, ChartBar, ListMagnifyingGlass, Broadcast, Power, Copy, Clock,
  Brain, ChartPieSlice, Question, CheckCircle, DownloadSimple, CalendarBlank, PaperPlaneRight,
} from "@phosphor-icons/react";
import { api, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHANNELS = [
  { id: "all", label: "Alla", icon: ListMagnifyingGlass },
  { id: "ai_chat", label: "AI-chatt", icon: ChatCircleDots },
  { id: "ai_voice", label: "AI-röst", icon: Headset },
  { id: "freshdesk", label: "Freshdesk", icon: EnvelopeSimple },
  { id: "zisson", label: "Zisson", icon: Phone },
  { id: "upload", label: "Uppladdade", icon: FileAudio },
];
const CH_LABEL = { ai_chat: "AI-chatt", ai_voice: "AI-röst", freshdesk: "Freshdesk", zisson: "Zisson", upload: "Uppladdad" };
const SENT = { happy: ["Nöjd", "bg-emerald-100 text-emerald-700"], neutral: ["Neutral", "bg-zinc-100 text-zinc-600"], frustrated: ["Frustrerad", "bg-amber-100 text-amber-700"], angry: ["Arg", "bg-red-100 text-red-700"] };
const RESOLVED = { yes: ["Löst", "bg-emerald-100 text-emerald-700"], no: ["Ej löst", "bg-red-100 text-red-700"], unclear: ["Oklart", "bg-zinc-100 text-zinc-600"] };
const SEV = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-emerald-100 text-emerald-700" };
const SEV_LABEL = { high: "Hög", medium: "Medel", low: "Låg" };
const IMPACT_LABEL = { high: "Hög effekt", medium: "Medel", low: "Låg" };

function Badge({ children, cls }) {
  return <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function CaseDetail({ data, onClose, onDownload }) {
  if (!data) return null;
  const [sl, sc] = SENT[data.sentiment] || SENT.neutral;
  const [rl, rc] = RESOLVED[data.resolved] || RESOLVED.unclear;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="case-detail-dialog" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-white rounded-2xl border border-zinc-200 p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge cls="bg-[#0033FF]/10 text-[#0033FF]">{CH_LABEL[data.channel] || data.channel}</Badge>
              <Badge cls="bg-zinc-100 text-zinc-700">{data.category}</Badge>
              <Badge cls={sc}>{sl}</Badge>
              <Badge cls={rc}>{rl}</Badge>
            </div>
            <h3 className="mt-2 font-display font-bold text-xl">{data.title}</h3>
          </div>
          <button onClick={onClose} data-testid="close-case-detail" className="text-zinc-400 hover:text-zinc-700"><X size={20} weight="bold" /></button>
        </div>
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Sammanfattning</div>
          <p className="mt-1 text-sm text-zinc-800 leading-relaxed" data-testid="case-summary">{data.summary}</p>
        </div>
        {data.customer_intent && (
          <div className="mt-4"><div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Kundens mål</div>
            <p className="mt-1 text-sm text-zinc-800">{data.customer_intent}</p></div>
        )}
        <div className="mt-4 bg-[#0033FF]/5 border border-[#0033FF]/15 rounded-lg p-4">
          <div className="text-xs font-bold uppercase tracking-[0.12em] text-[#0033FF]">Rekommenderad åtgärd</div>
          <p className="mt-1 text-sm text-zinc-800 leading-relaxed">{data.recommended_action}</p>
        </div>
        {data.transcript && (
          <details className="mt-4 group">
            <summary className="cursor-pointer text-sm font-semibold text-zinc-600 hover:text-zinc-900">Visa transkript</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3 max-h-64 overflow-y-auto">{data.transcript}</pre>
          </details>
        )}
        <button onClick={() => onDownload(data)} data-testid="download-case-pdf"
          className="mt-5 inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors">
          <FileArrowDown size={17} weight="bold" /> Ladda ner PDF
        </button>
      </div>
    </div>
  );
}

function CasesView({ canRun }) {
  const [channel, setChannel] = useState("all");
  const [feed, setFeed] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploadCh, setUploadCh] = useState("zisson");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (ch) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai-insights/feed?channel=${ch}`);
      setFeed(data);
    } catch (e) { toast.error("Kunde inte hämta ärenden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(channel); }, [channel, load]);

  const analyze = async (item) => {
    setBusyId(item.source_id);
    try {
      const { data } = await api.post("/ai-insights/analyze", { source_type: item.source_type, source_id: item.source_id });
      setDetail(data);
      load(channel);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analys misslyckades"); }
    finally { setBusyId(null); }
  };

  const openAnalyzed = async (item) => {
    // item already analyzed → find the case by source_id
    try {
      const { data } = await api.get(`/ai-insights/cases?channel=${item.channel}`);
      const c = data.find((x) => x.source_id === item.source_id);
      if (c) { const full = await api.get(`/ai-insights/cases/${c.id}`); setDetail(full.data); }
      else analyze(item);
    } catch { analyze(item); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("channel", uploadCh);
    fd.append("language", "Swedish");
    try {
      const { data } = await api.post("/ai-insights/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Inspelningen transkriberades och analyserades");
      setDetail(data);
      load(channel);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Uppladdning misslyckades"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (c) => {
    try { await downloadFile(`/ai-insights/cases/${c.id}/pdf`, `senzum-arende-${c.id}.pdf`); }
    catch (e) { toast.error("Nedladdning misslyckades"); }
  };

  return (
    <div>
      <CaseDetail data={detail} onClose={() => setDetail(null)} onDownload={download} />

      {/* Upload */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><FileAudio size={20} weight="bold" className="text-[#0033FF]" /></div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold">Ladda upp en samtalsinspelning</div>
            <div className="text-xs text-zinc-500">AI transkriberar (Whisper) och analyserar samtalet. mp3, m4a, wav, webm · max 25 MB.</div>
          </div>
          <select data-testid="upload-channel-select" value={uploadCh} onChange={(e) => setUploadCh(e.target.value)}
            className="text-sm bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#0033FF]/20 outline-none">
            <option value="zisson">Zisson (samtal)</option>
            <option value="freshdesk">Freshdesk</option>
            <option value="upload">Övrigt</option>
          </select>
          <input ref={fileRef} type="file" accept=".mp3,.m4a,.wav,.webm,.mp4,.mpeg,.mpga" onChange={onUpload} className="hidden" data-testid="upload-audio-input" />
          <button onClick={() => fileRef.current?.click()} disabled={!canRun || uploading} data-testid="upload-audio-button"
            title={!canRun ? "Du har inte behörighet" : undefined}
            className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? <CircleNotch size={17} className="animate-spin" /> : <UploadSimple size={17} weight="bold" />} {uploading ? "Bearbetar…" : "Välj fil"}
          </button>
        </div>
      </div>

      {/* Channel filter */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="channel-filter">
        {CHANNELS.map((c) => {
          const n = c.id === "all" ? feed.total : (feed.counts?.[c.id] ?? 0);
          const active = channel === c.id;
          return (
            <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setChannel(c.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${active ? "bg-[#0033FF] text-white border-[#0033FF]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>
              <c.icon size={15} weight="bold" /> {c.label}<span className={`text-xs ${active ? "text-white/70" : "text-zinc-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div className="mt-4 bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><CircleNotch size={26} className="animate-spin text-[#0033FF]" /></div>
        ) : feed.items.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500" data-testid="feed-empty">
            Inga ärenden i den här kanalen ännu. {channel === "freshdesk" || channel === "zisson" ? "Koppla integrationen eller ladda upp en inspelning." : "Ärenden dyker upp här när kunder hör av sig."}
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {feed.items.map((it) => (
              <div key={`${it.source_type}-${it.source_id}`} className="px-5 py-3.5 flex items-center gap-3" data-testid="feed-item">
                <Badge cls="bg-zinc-100 text-zinc-600">{CH_LABEL[it.channel] || it.channel}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{it.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{it.snippet || "—"}</div>
                </div>
                {it.analyzed ? (
                  <button onClick={() => openAnalyzed(it)} data-testid={`view-analysis-${it.source_id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0033FF] hover:underline">
                    <Sparkle size={15} weight="fill" /> Visa analys
                  </button>
                ) : (
                  <button onClick={() => analyze(it)} disabled={!canRun || busyId === it.source_id} data-testid={`summarize-${it.source_id}`}
                    title={!canRun ? "Du har inte behörighet" : undefined}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-black text-white rounded-md px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {busyId === it.source_id ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="bold" />} Sammanfatta
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportView({ canRun }) {
  const [period, setPeriod] = useState("30");
  const [channel, setChannel] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ai-insights/report/latest").then((r) => setReport(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const body = { period, channel };
      if (period === "custom") { body.date_from = from; body.date_to = to; }
      const { data } = await api.post("/ai-insights/report", body);
      setReport(data);
      toast.success("Rapport genererad");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte generera rapport"); }
    finally { setLoading(false); }
  };

  const download = async () => {
    if (!report?.id) return;
    try { await downloadFile(`/ai-insights/report/${report.id}/pdf`, `senzum-cx-rapport.pdf`); }
    catch { toast.error("Nedladdning misslyckades"); }
  };

  const maxCount = Math.max(1, ...((report?.top_case_types || []).map((t) => t.count || 0)));

  return (
    <div>
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Period</label>
            <div className="mt-1.5 flex gap-1.5">
              {["7", "30", "90", "custom"].map((p) => (
                <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
                  className={`rounded-md px-3 py-2 text-sm font-semibold border transition-colors ${period === p ? "bg-[#0033FF] text-white border-[#0033FF]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>
                  {p === "custom" ? "Eget" : `${p} dagar`}
                </button>
              ))}
            </div>
          </div>
          {period === "custom" && (
            <div className="flex items-end gap-2">
              <div><label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Från</label>
                <input data-testid="date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 block bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0033FF]/20" /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Till</label>
                <input data-testid="date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1.5 block bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0033FF]/20" /></div>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Kanal</label>
            <select data-testid="report-channel-select" value={channel} onChange={(e) => setChannel(e.target.value)}
              className="mt-1.5 block bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0033FF]/20">
              {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.id === "all" ? "Alla kanaler" : c.label}</option>)}
            </select>
          </div>
          <button onClick={generate} disabled={!canRun || loading} data-testid="generate-report-button"
            title={!canRun ? "Du har inte behörighet" : undefined}
            className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px]">
            {loading ? <CircleNotch size={17} className="animate-spin" /> : <Sparkle size={17} weight="bold" />} Generera rapport
          </button>
        </div>
      </div>

      {!report ? (
        <div className="mt-6 bg-white border border-dashed border-zinc-300 rounded-xl p-12 text-center" data-testid="report-empty">
          <ChartBar size={30} weight="duotone" className="text-[#0033FF] mx-auto" />
          <p className="mt-3 text-sm text-zinc-500">Generera en rapport för att se vanligaste ärendetyperna, trender och konkreta tips för att minska inflödet.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-5" data-testid="report-content">
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Sammanfattning · {report.period_label} · {report.analyzed_count} ärenden</div>
                <p className="mt-2 text-zinc-800 leading-relaxed">{report.summary}</p>
              </div>
              <button onClick={download} data-testid="download-report-pdf"
                className="shrink-0 inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors">
                <FileArrowDown size={17} weight="bold" /> Ladda ner PDF
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(report.channel_breakdown || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                <Badge key={k} cls="bg-zinc-100 text-zinc-600">{CH_LABEL[k] || k}: {v}</Badge>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><ChartBar size={18} weight="bold" className="text-[#0033FF]" /> Vanligaste ärendetyperna</h3>
              <div className="mt-4 space-y-3">
                {(report.top_case_types || []).map((t, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-zinc-800">{t.title}</span>
                      <span className="flex items-center gap-2"><Badge cls={SEV[t.severity] || SEV.medium}>{SEV_LABEL[t.severity] || "—"}</Badge><span className="font-mono text-zinc-500">{t.count}</span></span>
                    </div>
                    <div className="mt-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#0033FF] rounded-full" style={{ width: `${Math.round((t.count / maxCount) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><TrendUp size={18} weight="bold" className="text-[#0033FF]" /> Trender</h3>
              <div className="mt-4 space-y-3">
                {(report.trends || []).map((t, i) => {
                  const Icon = t.direction === "up" ? TrendUp : t.direction === "down" ? TrendDown : ArrowRight;
                  const col = t.direction === "up" ? "text-red-500" : t.direction === "down" ? "text-emerald-600" : "text-zinc-400";
                  return (
                    <div key={i} className="flex gap-2.5">
                      <Icon size={18} weight="bold" className={`${col} mt-0.5 shrink-0`} />
                      <div><div className="text-sm font-semibold text-zinc-800">{t.title}</div><div className="text-xs text-zinc-500">{t.detail}</div></div>
                    </div>
                  );
                })}
                {(!report.trends || report.trends.length === 0) && <p className="text-sm text-zinc-400">Inga tydliga trender ännu.</p>}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0033FF]/[0.04] to-transparent border border-[#0033FF]/15 rounded-xl p-5">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Lightbulb size={18} weight="fill" className="text-[#0033FF]" /> Konkreta tips för att minska inflödet</h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {(report.deflection_tips || []).map((t, i) => (
                <div key={i} className="bg-white border border-zinc-200 rounded-lg p-3.5" data-testid="deflection-tip">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-zinc-900">{t.title}</span>
                    <Badge cls={SEV[t.impact] || SEV.medium}>{IMPACT_LABEL[t.impact] || "—"}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-zinc-600 leading-relaxed">{t.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {(report.recommendations || []).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold text-lg">Rekommendationer</h3>
              <ul className="mt-3 space-y-2">
                {report.recommendations.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-800"><span className="text-[#0033FF] font-bold">•</span>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function ZissonView({ canRun, canManage }) {
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [info, setInfo] = useState(null);
  const [label, setLabel] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [endsAt, setEndsAt] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [st, ss] = await Promise.all([
        api.get("/ai-insights/zisson/status"),
        api.get("/ai-insights/zisson/sessions"),
      ]);
      setStatus(st.data);
      setSessions(ss.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    refresh();
    if (canManage) api.get("/ai-insights/zisson/ingest-info").then((r) => setInfo(r.data)).catch(() => {});
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh, canManage]);

  const start = async () => {
    setBusy(true);
    try {
      const body = { label };
      if (schedule && endsAt) body.ends_at = new Date(endsAt).toISOString();
      await api.post("/ai-insights/zisson/start", body);
      toast.success("Lyssningsperiod startad");
      setLabel(""); setSchedule(false); setEndsAt("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte starta"); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/ai-insights/zisson/stop");
      toast.success("Period avslutad – rapport genererad");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte avsluta"); }
    finally { setBusy(false); }
  };

  const downloadReport = async (rid) => {
    try { await downloadFile(`/ai-insights/report/${rid}/pdf`, `senzum-zisson-rapport.pdf`); }
    catch { toast.error("Nedladdning misslyckades"); }
  };

  const active = status?.active;

  return (
    <div className="space-y-5" data-testid="zisson-view">
      {/* Control card */}
      <div className={`rounded-xl p-5 border ${active ? "border-[#0033FF]/30 bg-[#0033FF]/[0.04]" : "border-zinc-200 bg-white"}`}>
        {active ? (
          <div data-testid="zisson-active">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0033FF] opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0033FF]" />
              </span>
              <h3 className="font-display font-bold text-lg">Lyssnar på inkommande Zisson-samtal</h3>
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Startade</div><div className="mt-0.5 font-semibold">{fmtDateTime(status.session.started_at)}</div></div>
              <div><div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Schemalagt slut</div><div className="mt-0.5 font-semibold">{status.session.ends_at ? fmtDateTime(status.session.ends_at) : "Manuellt"}</div></div>
              <div><div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Fångade samtal</div><div className="mt-0.5 font-semibold text-[#0033FF]" data-testid="zisson-captured">{status.captured}</div></div>
            </div>
            <button onClick={stop} disabled={!canRun || busy} data-testid="zisson-stop-button"
              title={!canRun ? "Du har inte behörighet" : undefined}
              className="mt-4 inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? <CircleNotch size={17} className="animate-spin" /> : <Power size={17} weight="bold" />} Stäng av & generera rapport
            </button>
          </div>
        ) : (
          <div data-testid="zisson-idle">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><Broadcast size={20} weight="bold" className="text-[#0033FF]" /></div>
              <div>
                <h3 className="font-display font-bold text-lg">Starta en lyssningsperiod</h3>
                <p className="text-xs text-zinc-500">AI lyssnar på alla inkommande Zisson-samtal under perioden och bygger en rapport när du stänger av.</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Namn (valfritt)</label>
                <input data-testid="zisson-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
                  className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0033FF]/20" placeholder="t.ex. Vecka 26 – supportkampanj" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-zinc-600 pb-2 cursor-pointer">
                <input data-testid="zisson-schedule-toggle" type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} className="h-4 w-4 accent-[#0033FF]" />
                Schemalägg slut
              </label>
              {schedule && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Avslutas</label>
                  <input data-testid="zisson-ends-at" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
                    className="mt-1.5 block bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0033FF]/20" />
                </div>
              )}
              <button onClick={start} disabled={!canRun || busy} data-testid="zisson-start-button"
                title={!canRun ? "Du har inte behörighet" : undefined}
                className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px]">
                {busy ? <CircleNotch size={17} className="animate-spin" /> : <Power size={17} weight="bold" />} Starta lyssning
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Webhook info */}
      {canManage && info && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="zisson-webhook-info">
          <h3 className="font-display font-bold text-base flex items-center gap-2"><Clock size={17} weight="bold" className="text-[#0033FF]" /> Koppla Zisson (webhook)</h3>
          <p className="mt-1 text-xs text-zinc-500">Låt Zisson posta varje samtals inspelning hit. Fångas automatiskt under aktiva perioder.</p>
          {[["Webhook-URL", info.webhook_url], ["Ingest-token", info.token]].map(([k, v]) => (
            <div key={k} className="mt-3">
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">{k}</div>
              <div className="mt-1 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
                <code className="flex-1 text-xs font-mono text-zinc-700 truncate">{v}</code>
                <button onClick={() => { navigator.clipboard.writeText(v); toast.success("Kopierat"); }} className="text-zinc-400 hover:text-[#0033FF]"><Copy size={16} weight="bold" /></button>
              </div>
            </div>
          ))}
          <p className="mt-2 text-xs text-zinc-400">{info.usage}</p>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-zinc-200 text-sm font-semibold text-zinc-500">Historik – tidigare perioder</div>
        {sessions.filter((s) => s.status === "stopped").length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="zisson-history-empty">Inga avslutade perioder ännu.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {sessions.filter((s) => s.status === "stopped").map((s) => (
              <div key={s.id} className="px-5 py-3.5 flex flex-wrap items-center gap-3" data-testid="zisson-history-row">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{s.label}</div>
                  <div className="text-xs text-zinc-500">{fmtDateTime(s.started_at)} → {fmtDateTime(s.stopped_at)}</div>
                </div>
                <Badge cls="bg-zinc-100 text-zinc-600">{s.captured} samtal</Badge>
                {s.report_id && (
                  <button onClick={() => downloadReport(s.report_id)} data-testid={`zisson-report-${s.id}`}
                    className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-black text-white rounded-md px-3 py-1.5 text-xs font-semibold transition-colors">
                    <FileArrowDown size={14} weight="bold" /> Rapport-PDF
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SENT_MAP = {
  positive: { label: "Positiv", color: "#10B981" },
  neutral: { label: "Neutral", color: "#64748B" },
  negative: { label: "Negativ", color: "#EF4444" },
};

function KundvarldView({ canRun, canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    api.get("/insights").then((r) => setData(r.data)).catch(() => setData(false)).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGen(true);
    try {
      const body = {};
      if (fromDate) body.from_date = fromDate;
      if (toDate) body.to_date = toDate;
      const { data: d } = await api.post("/insights/generate", body);
      setData(d);
      toast.success("Kundvärlden uppdaterad");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kunde inte analysera");
    } finally { setGen(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/insights/report-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Senzum_AI_Insights.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Kunde inte ladda ner rapporten"); } finally { setDownloading(false); }
  };

  const has = data && data.generated_at;
  const maxQ = Math.max(1, ...((data?.top_questions || []).map((q) => q.count || 0)));
  const maxC = Math.max(1, ...((data?.categories || []).map((c) => c.count || 0)));

  return (
    <div className="space-y-5" data-testid="kundvarld-view">
      <div className="rounded-xl border border-zinc-200 bg-gradient-to-br from-[#0033FF]/6 to-transparent p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#0033FF] flex items-center justify-center shrink-0"><Brain size={24} weight="fill" className="text-white" /></div>
            <div>
              <h2 className="font-display font-bold text-xl">Kundvärld</h2>
              <p className="text-sm text-zinc-500 mt-0.5 max-w-xl">AI:n läser alla dina kundsamtal och visar hur kunderna mår, vad de frågar om mest och vilka kategorier som dominerar – så du kan fatta beslut på riktig data.
                {has ? ` Senast: ${(data.generated_at || "").slice(0, 16).replace("T", " ")} · ${data.analyzed_messages || 0} meddelanden.` : ""}</p>
            </div>
          </div>
          {canRun && (
            <button data-testid="generate-kundvarld" onClick={generate} disabled={gen}
              className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-60 whitespace-nowrap">
              {gen ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
              {has ? "Uppdatera analys" : "Aktivera AI-insikter"}
            </button>
          )}
        </div>
        {canRun && (
          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-zinc-200/70">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 flex items-center gap-1"><CalendarBlank size={12} weight="bold" /> Från</label>
              <input type="date" data-testid="insights-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Till</label>
              <input type="date" data-testid="insights-to" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="mt-1 bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
            </div>
            {(fromDate || toDate) && (
              <button onClick={() => { setFromDate(""); setToDate(""); }} className="text-sm text-zinc-500 hover:text-zinc-800 pb-2">Rensa</button>
            )}
            {has && (
              <button data-testid="insights-download-pdf" onClick={downloadPdf} disabled={downloading}
                className="ml-auto inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 font-semibold text-sm disabled:opacity-60">
                {downloading ? <CircleNotch size={15} className="animate-spin" /> : <DownloadSimple size={15} weight="bold" />} Ladda ner PDF
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-400"><CircleNotch size={26} className="animate-spin mx-auto" /></div>
      ) : !has ? (
        <div className="text-center py-14 border border-dashed border-zinc-200 rounded-xl" data-testid="kundvarld-empty">
          <Brain size={34} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
          <p className="text-sm text-zinc-500">Inga insikter än. Klicka <b>Aktivera AI-insikter</b> så analyserar AI:n dina kundsamtal.</p>
        </div>
      ) : (
        <>
          {data.summary && (
            <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="kundvarld-summary">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1.5"><Brain size={13} weight="bold" /> Sammanfattning</div>
              <p className="text-[15px] text-zinc-700 leading-relaxed">{data.summary}</p>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Question size={18} weight="fill" className="text-[#0033FF]" /> Mest ställda frågor</h3>
              <div className="space-y-3">
                {(data.top_questions || []).length === 0 && <p className="text-sm text-zinc-400">Inga frågor identifierade.</p>}
                {(data.top_questions || []).map((q, i) => (
                  <div key={i} data-testid="kundvarld-question">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-zinc-700">{q.question}</span>
                      <span className="text-zinc-400 font-semibold shrink-0">{q.count}</span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full mt-1.5 overflow-hidden">
                      <div className="h-full bg-[#0033FF] rounded-full" style={{ width: `${Math.round((q.count / maxQ) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold flex items-center gap-2 mb-4"><ChartPieSlice size={18} weight="fill" className="text-[#0033FF]" /> Kategorier kunderna hör av sig om</h3>
              <div className="space-y-3">
                {(data.categories || []).length === 0 && <p className="text-sm text-zinc-400">Inga kategorier identifierade.</p>}
                {(data.categories || []).map((c, i) => {
                  const s = SENT_MAP[c.sentiment] || SENT_MAP.neutral;
                  return (
                    <div key={i} data-testid="kundvarld-category">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-zinc-700 font-medium">{c.name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: s.color, background: `${s.color}1A` }}>{s.label}</span>
                          <span className="text-zinc-400 font-semibold">{c.count}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full mt-1.5 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.round((c.count / maxC) * 100)}%`, background: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {(data.themes || []).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-display font-bold flex items-center gap-2 mb-4"><TrendUp size={18} weight="bold" className="text-[#0033FF]" /> Framväxande teman</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.themes.map((th, i) => {
                  const sev = { high: "#EF4444", medium: "#F59E0B", low: "#10B981" }[th.severity] || "#F59E0B";
                  return (
                    <div key={i} data-testid="kundvarld-theme" className="border border-zinc-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-zinc-800">{th.title}</span>
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ color: sev, background: `${sev}1A` }}>{th.severity}</span>
                      </div>
                      {th.example && <p className="text-xs text-zinc-500 mt-1 italic">"{th.example}"</p>}
                      <div className="text-xs text-zinc-400 mt-1">~{th.count} meddelanden</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(data.recommendations || []).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="kundvarld-recs">
              <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Lightbulb size={18} weight="fill" className="text-amber-500" /> Rekommendationer för dina beslut</h3>
              <ul className="space-y-2">
                {data.recommendations.map((r, i) => (
                  <li key={i} className="text-sm text-zinc-700 flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#0033FF] mt-0.5 shrink-0" />{r}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {canManage && <ScheduleCard />}
    </div>
  );
}

function ScheduleCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const DAYS = ["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"];

  useEffect(() => {
    api.get("/insights/schedule").then((r) => setS(r.data)).catch(() => setS(false));
  }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    // Defer persisting an enabled schedule until at least one recipient exists, so the
    // inputs can be revealed first. Avoids a 400 on the very first toggle (chicken-and-egg).
    if (next.enabled && !(next.recipient_email || "").trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/insights/schedule", {
        enabled: next.enabled, recipient_email: next.recipient_email || "", weekday: next.weekday ?? 0,
      });
      setS(data);
      toast.success(data.enabled ? "Veckorapport schemalagd" : "Schemaläggning avstängd");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kunde inte spara");
      api.get("/insights/schedule").then((r) => setS(r.data)).catch(() => {});
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/insights/schedule/test");
      toast.success(`Testrapport skickad till ${(data.recipients || []).join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kunde inte skicka testrapport");
    } finally { setTesting(false); }
  };

  if (!s) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="insights-schedule-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#0033FF]/10 flex items-center justify-center shrink-0"><EnvelopeSimple size={20} weight="fill" className="text-[#0033FF]" /></div>
          <div>
            <h3 className="font-display font-bold">Schemalagd veckorapport via mejl</h3>
            <p className="text-sm text-zinc-500 mt-0.5 max-w-lg">Få veckans AI-sammanfattning, nyckeltal och trendlarm automatiskt mejlade. Avstängd som standard.</p>
          </div>
        </div>
        <button data-testid="schedule-toggle" onClick={() => save({ enabled: !s.enabled })} disabled={saving}
          className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${s.enabled ? "bg-[#0033FF]" : "bg-zinc-300"}`}>
          <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${s.enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>
      {s.enabled && (
        <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-zinc-100">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Mottagare (flera separeras med komma)</label>
            <input data-testid="schedule-email" type="text" value={s.recipient_email || ""}
              onChange={(e) => setS({ ...s, recipient_email: e.target.value })}
              onBlur={() => save({ recipient_email: s.recipient_email })}
              placeholder="chef@foretag.se, support@foretag.se" className="mt-1 w-full bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Skickas varje</label>
            <select data-testid="schedule-weekday" value={s.weekday ?? 0} onChange={(e) => save({ weekday: Number(e.target.value) })}
              className="mt-1 bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none">
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <button data-testid="schedule-test" onClick={sendTest} disabled={testing}
            className="inline-flex items-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {testing ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneRight size={15} weight="fill" />} Skicka testrapport nu
          </button>
          {s.last_sent && <p className="text-xs text-zinc-400 pb-2 w-full">Senast skickad: {(s.last_sent || "").slice(0, 16).replace("T", " ")}</p>}
        </div>
      )}
    </div>
  );
}

export default function AiInsights() {
  const { can } = useAuth();
  const canRun = can("run_costly");
  const canManage = can("manage_integrations");
  const [tab, setTab] = useState("kundvarld");

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="ai-insights-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#0033FF] flex items-center justify-center"><Sparkle size={22} weight="fill" className="text-white" /></div>
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">AI-insights</h1>
          <p className="mt-0.5 text-zinc-500 text-sm">Din hjärna in i kundernas värld – förstå hur de mår, vad de frågar om mest och fatta bättre beslut.</p>
        </div>
      </div>

      <div className="mt-6 flex w-full flex-wrap justify-center gap-1 bg-zinc-900 border border-white/10 rounded-xl p-1.5 shadow-sm" data-testid="insights-tabs">
        <button onClick={() => setTab("kundvarld")} data-testid="tab-kundvarld"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${tab === "kundvarld" ? "bg-[#0033FF] text-white shadow-sm" : "text-white/90 hover:bg-white/10"}`}>
          Kundvärld
        </button>
        <button onClick={() => setTab("cases")} data-testid="tab-cases"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${tab === "cases" ? "bg-[#0033FF] text-white shadow-sm" : "text-white/90 hover:bg-white/10"}`}>
          Ärenden
        </button>
        <button onClick={() => setTab("report")} data-testid="tab-report"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${tab === "report" ? "bg-[#0033FF] text-white shadow-sm" : "text-white/90 hover:bg-white/10"}`}>
          CX-rapport
        </button>
        <button onClick={() => setTab("zisson")} data-testid="tab-zisson"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${tab === "zisson" ? "bg-[#0033FF] text-white shadow-sm" : "text-white/90 hover:bg-white/10"}`}>
          Zisson-lyssning
        </button>
      </div>

      <div className="mt-5">
        {tab === "kundvarld" && <KundvarldView canRun={canRun} canManage={canManage} />}
        {tab === "cases" && <CasesView canRun={canRun} />}
        {tab === "report" && <ReportView canRun={canRun} />}
        {tab === "zisson" && <ZissonView canRun={canRun} canManage={canManage} />}
      </div>
    </div>
  );
}
